/*
  Example component for API endpoint testing - migrated from jwt-bch-frontend
*/

import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Card, Form, Button } from 'react-bootstrap'
import JSONPretty from 'react-json-pretty'
import fetchToCurl from 'fetch-to-curl'
import { CircularProgress } from '@mui/material'

function Example ({ data, SERVER, VERSION }) {
  const [endpointType, setEndpointType] = useState('')
  const [parameterType, setParameterType] = useState('')
  const [parameters, setParameters] = useState('')
  const [detailedDescription, setDetailedDescription] = useState('')
  const [parameterDescription, setParameterDescription] = useState('')
  const [requestBody, setRequestBody] = useState('')
  const [endpoint, setEndpoint] = useState('')
  const [tryItOut, setTryItOut] = useState(false)
  const [requestResult, setRequestResult] = useState('')
  const [curl, setCurl] = useState('')
  const [requestUrl, setRequestUrl] = useState('')
  const [responseCode, setResponseCode] = useState('')
  const [inFetch, setInFetch] = useState(false)

  useEffect(() => {
    if (data) {
      setEndpointType(data.type || '')
      setParameterType(data.type === 'get' || data.type === 'GET' ? 'string' : 'array')
      setParameters(data.parameters || '')
      setDetailedDescription(data.detailedDescription || '')
      setParameterDescription(data.parameterDescription || '')
      setRequestBody(data.body ? JSON.stringify(data.body, null, 2) : '')
      setEndpoint(data.endpoint || '')
    }
  }, [data])

  const handleUpdate = (e) => {
    const { name, value } = e.target
    if (name === 'requestBody') {
      setRequestBody(value)
    } else if (name === 'parameters') {
      setParameters(value)
    }
  }

  const handleTryItOut = () => {
    clearRequestResult()
    setTryItOut(!tryItOut)
  }

  const clearRequestResult = () => {
    setRequestResult('')
    setCurl('')
    setRequestUrl('')
    setResponseCode('')
    if (data.body) {
      setRequestBody(JSON.stringify(data.body, null, 2))
    }
    if (data.parameters) {
      setParameters(data.parameters)
    }
  }

  const startRequest = async () => {
    setInFetch(true)
    try {
      if (endpointType === 'get' || endpointType === 'GET') {
        await requestGET()
      } else if (endpointType === 'post' || endpointType === 'POST') {
        await requestPOST()
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setInFetch(false)
    }
  }

  const requestPOST = async () => {
    const endpointUrl = `${SERVER}${VERSION}${endpoint}`

    let body
    try {
      body = JSON.parse(requestBody)
    } catch (error) {
      // If parsing fails, use the string as-is
      body = requestBody
    }

    try {
      const options = {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: typeof body === 'string' ? body : JSON.stringify(body)
      }

      const result = await fetch(endpointUrl, options)
      const code = result.status
      const data = await result.text()

      const curlCommand = fetchToCurl(endpointUrl, options)
      setRequestResult(data)
      setCurl(curlCommand)
      setRequestUrl(endpointUrl)
      setResponseCode(code)
    } catch (err) {
      console.error('POST request error:', err)
    }
  }

  const requestGET = async () => {
    const endpointUrl = `${SERVER}${VERSION}${endpoint}${parameters}`

    try {
      const options = {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json'
        }
      }

      const result = await fetch(endpointUrl, options)
      const code = result.status
      const data = await result.text()

      const curlCommand = fetchToCurl(endpointUrl, options)
      setRequestResult(data)
      setCurl(curlCommand)
      setRequestUrl(endpointUrl)
      setResponseCode(code)
    } catch (err) {
      console.error('GET request error:', err)
    }
  }

  return (
    <Card className='mt-3'>
      <Card.Body>
        <p>{detailedDescription}</p>

        <div className='mb-3'>
          <strong>Parameters:</strong>
          <p>{parameters ? parameterType : 'No Parameters'}</p>
        </div>

        <div className='mb-3'>
          <strong>Description:</strong>
          <p>{parameterDescription}</p>
        </div>

        {requestBody && (
          <div className='mb-3'>
            <strong>Example Body:</strong>
            {!tryItOut
              ? (
                <JSONPretty id='json-pretty' data={requestBody} />
                )
              : (
                <Form.Control
                  as='textarea'
                  rows={4}
                  name='requestBody'
                  value={requestBody}
                  onChange={handleUpdate}
                  placeholder='Enter your data'
                />
                )}
          </div>
        )}

        {parameters && tryItOut && (
          <div className='mb-3'>
            <strong>Example Parameter:</strong>
            <Form.Control
              type='text'
              name='parameters'
              value={parameters}
              onChange={handleUpdate}
              placeholder='Enter your data'
            />
          </div>
        )}

        <div className='mb-3'>
          {!tryItOut
            ? (
              <Button onClick={handleTryItOut} variant='primary'>
                Try it Out
              </Button>
              )
            : (
              <div className='d-flex gap-2'>
                <Button onClick={handleTryItOut} variant='secondary'>
                  Cancel
                </Button>
                <Button onClick={startRequest} variant='primary' disabled={inFetch}>
                  Execute
                </Button>
              </div>
              )}
        </div>

        {inFetch && (
          <div className='text-center mb-3'>
            <CircularProgress />
          </div>
        )}

        {curl && (
          <div className='mb-3'>
            <strong>Curl:</strong>
            <pre className='bg-light p-2 rounded'>{curl}</pre>
          </div>
        )}

        {requestUrl && (
          <div className='mb-3'>
            <strong>Request Url:</strong>
            <p className='text-break'>{requestUrl}</p>
          </div>
        )}

        {responseCode && (
          <div className='mb-3'>
            <strong>Response Code:</strong>
            <p>{responseCode}</p>
          </div>
        )}

        {requestResult && (
          <>
            <div className='mb-3'>
              <strong>Response Body:</strong>
              <JSONPretty id='json-pretty' data={requestResult} />
            </div>
            <Button onClick={clearRequestResult} variant='outline-secondary'>
              Clear
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  )
}

Example.propTypes = {
  data: PropTypes.object,
  SERVER: PropTypes.string,
  VERSION: PropTypes.string
}

export default Example
